import { useMemo, useState } from 'react';
import { CalendarDays, PackageCheck, X } from 'lucide-react';
import { createPackagePurchase } from './data.js';
import { friendlyError, number, rupiah } from './lib/format.js';

const EMPTY_ROWS = [];

export function PackagePurchaseModal({ references, onClose, onSaved }) {
  const clients = references.clients || EMPTY_ROWS;
  const packages = references.packages || EMPTY_ROWS;
  const [clientId, setClientId] = useState('');
  const [packageId, setPackageId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const client = useMemo(() => clients.find((row) => row.id === clientId), [clients, clientId]);
  const selectedPackage = useMemo(() => packages.find((row) => row.id === packageId), [packages, packageId]);

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (!client || !selectedPackage) {
      setError('Pasien dan paket wajib dipilih.');
      return;
    }
    const question = `Buat tagihan ${selectedPackage.package_name} untuk ${client.name} sebesar ${rupiah.format(Number(selectedPackage.package_price || 0))}?\n\nSaldo paket baru aktif setelah pembayaran lunas.`;
    if (!window.confirm(question)) return;

    setBusy(true);
    try {
      const result = await createPackagePurchase(client.id, selectedPackage.id);
      onSaved(result, `Tagihan ${result.invoice_number} dibuat. Paket belum aktif sampai pembayaran lunas.`);
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <form className="modal-card modal-card-package" role="dialog" aria-modal="true" aria-label="Pembelian paket" onSubmit={submit}>
        <header className="modal-head">
          <div className="modal-title-icon"><PackageCheck size={23} /></div>
          <div className="grow">
            <h2>Pembelian Paket</h2>
            <p>Buat tagihan terlebih dahulu. Saldo paket aktif otomatis setelah pembayaran lunas.</p>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Tutup"><X size={20} /></button>
        </header>

        <div className="modal-body package-purchase-body">
          {error ? <div className="alert alert-danger">{error}</div> : null}
          {!packages.length ? <div className="alert alert-danger">Belum ada master paket aktif dengan layanan dan kuota yang valid.</div> : null}

          <div className="form-grid">
            <label className="form-field">
              <span>Pasien *</span>
              <select value={clientId} onChange={(event) => setClientId(event.target.value)} disabled={busy} required>
                <option value="">Pilih pasien…</option>
                {clients.map((row) => (
                  <option key={row.id} value={row.id}>{row.name} · {row.client_code}{row.phone ? ` · ${row.phone}` : ''}</option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>Master paket *</span>
              <select value={packageId} onChange={(event) => setPackageId(event.target.value)} disabled={busy || !packages.length} required>
                <option value="">Pilih paket…</option>
                {packages.map((row) => (
                  <option key={row.id} value={row.id}>{row.package_name} · {rupiah.format(Number(row.package_price || 0))}</option>
                ))}
              </select>
            </label>
          </div>

          {client ? (
            <section className="selected-client">
              <strong>{client.name}</strong>
              <span>{client.client_code}{client.birth_date ? ` · Lahir ${client.birth_date}` : ''}{client.phone ? ` · ${client.phone}` : ''}</span>
            </section>
          ) : null}

          {selectedPackage ? (
            <section className="package-preview">
              <div className="package-preview-head">
                <div>
                  <span className="eyebrow">Paket dipilih</span>
                  <h3>{selectedPackage.package_name}</h3>
                </div>
                <strong>{rupiah.format(Number(selectedPackage.package_price || 0))}</strong>
              </div>
              <div className="package-preview-meta">
                <span><PackageCheck size={16} /> {number.format(Number(selectedPackage.total_sessions || 0))} total sesi</span>
                <span><CalendarDays size={16} /> {Number(selectedPackage.valid_days || 0) > 0 ? `Berlaku ${number.format(selectedPackage.valid_days)} hari` : 'Tanpa batas hari'}</span>
              </div>
              <div className="package-quota-list">
                {(selectedPackage.items || []).map((item) => (
                  <div key={item.service_id}>
                    <span>{item.service_name}</span>
                    <strong>{number.format(Number(item.quota || 0))} sesi</strong>
                  </div>
                ))}
              </div>
              <p className="muted package-activation-note">Kuota belum dapat digunakan sebelum invoice berstatus PAID.</p>
            </section>
          ) : null}
        </div>

        <footer className="modal-actions">
          <button type="button" className="button button-quiet" onClick={onClose} disabled={busy}>Tutup</button>
          <button className="button button-primary" disabled={busy || !client || !selectedPackage}>
            <PackageCheck size={17} /> {busy ? 'Membuat tagihan…' : 'Buat Tagihan Paket'}
          </button>
        </footer>
      </form>
    </div>
  );
}
